import styled from "styled-components";

import { DATA_E2E_KEY, IStyledAttributes } from "@ryde/shared";
import {
  blueGreyMui,
  blueMui,
  deepPurpleMui,
  greenMui,
  greyMui,
  redMui,
  rydeColors,
} from "@ryde/theme";

export const StationTimingPreviewWrapper = styled.section.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "station-timing-preview-wrapper",
  },
)`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 4px;
  min-width: min(680px, 100%);

  .station-timing__toolbar {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
  }

  .station-timing__bulk-button {
    min-height: 44px;
    min-width: 44px;
    padding: 8px 14px;
    border: 1px solid ${blueGreyMui._200};
    border-radius: 6px;
    background-color: ${rydeColors.white};
    color: ${blueGreyMui._800};
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
      background-color: ${blueGreyMui._50};
    }

    &:focus-visible {
      outline: 2px solid ${greyMui.A700};
      outline-offset: 2px;
    }

    @media (prefers-reduced-motion: no-preference) {
      transition: background-color 0.2s ease;
    }
  }

  .station-timing__status {
    margin: 0;
    font-size: 13px;
    line-height: 1.45;
    color: ${blueGreyMui._700};
  }

  .station-timing__basis {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.45;
    color: ${blueGreyMui._900};
  }

  .station-timing__note {
    margin: 0;
    padding: 8px 10px;
    border-inline-start: 3px solid ${blueGreyMui._300};
    border-radius: 4px;
    background-color: ${blueGreyMui._50};
    font-size: 12px;
    line-height: 1.45;
    color: ${blueGreyMui._800};
  }

  .station-timing__scroll {
    overflow-x: auto;
    border: 1px solid ${blueGreyMui._100};
    border-radius: 8px;
  }

  .station-timing__table {
    width: 100%;
    /* Fixed layout is what stops the leg names from stealing the measurements column; the shared
       minimum keeps every column readable and hands anything narrower to the scroll container. */
    table-layout: fixed;
    min-width: 920px;
    border-collapse: collapse;
    background-color: ${rydeColors.white};
    font-size: 12.5px;
    font-variant-numeric: tabular-nums;
    color: ${blueGreyMui._900};
  }

  .station-timing__col-leg {
    width: 26%;
  }

  .station-timing__col-planned {
    width: 9%;
  }

  .station-timing__col-measure {
    width: 42%;
  }

  .station-timing__col-delta {
    width: 12%;
  }

  .station-timing__col-based-on {
    width: 11%;
  }

  .station-timing__caption,
  .station-timing__visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }

  .station-timing__table th,
  .station-timing__table td {
    padding: 8px;
    text-align: start;
    vertical-align: middle;
    border-bottom: 1px solid ${blueGreyMui._50};
  }

  .station-timing__table thead th {
    padding-block: 6px;
    border-bottom: 1px solid ${blueGreyMui._100};
    color: ${blueGreyMui._700};
    font-size: 11px;
    font-weight: 600;
    line-height: 1.3;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  /* The no-data group keeps its markup while collapsed, so hiding it has to beat the row-group display */
  .station-timing__table tbody[hidden] {
    display: none;
  }

  .station-timing__table tbody:last-of-type tr:last-child th,
  .station-timing__table tbody:last-of-type tr:last-child td {
    border-bottom: none;
  }

  .station-timing__leg {
    font-weight: 600;
    line-height: 1.35;
    color: ${blueGreyMui._900};
    overflow-wrap: anywhere;
  }

  .station-timing__leg-index {
    display: inline-block;
    min-width: 20px;
    margin-inline-end: 4px;
    color: ${blueGreyMui._600};
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }

  .station-timing__leg-arrow {
    display: inline-block;
    color: ${blueGreyMui._600};
    padding: 0 4px;
  }

  /* The glyph itself is not mirrored by the RTL plugin, so flip it with the layout */
  html[dir="rtl"] & .station-timing__leg-arrow {
    transform: scaleX(-1);
  }

  .station-timing__numeric,
  .station-timing__hours {
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    /* Durations, signed gaps and hour windows read left to right in every locale */
    direction: ltr /* @noflip */;
    unicode-bidi: embed;
  }

  .station-timing__table .station-timing__planned,
  .station-timing__table .station-timing__delta-cell,
  .station-timing__table .station-timing__based-on,
  .station-timing__table .station-timing__head-end {
    text-align: end;
  }

  .station-timing__planned {
    color: ${blueGreyMui._800};
    font-weight: 600;
  }

  /* The tallest cell of a leg, so it sets the row's rhythm: 25 legs have to stay scannable. */
  .station-timing__measure {
    padding-block: 6px;
  }

  .station-timing__based-on {
    color: ${blueGreyMui._900};
    font-weight: 600;
    white-space: nowrap;
  }

  .station-timing__based-on-unit {
    display: block;
    margin-block-start: 1px;
    color: ${blueGreyMui._700};
    font-size: 11px;
    font-weight: 400;
  }

  .station-timing__no-data {
    color: ${blueGreyMui._700};
    font-style: italic;
  }

  .station-timing__expander-cell {
    padding: 0;
    background-color: ${blueGreyMui._50};
  }

  .station-timing__expander {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    min-height: 44px;
    padding: 8px 10px;
    border: none;
    background-color: transparent;
    color: ${blueGreyMui._800};
    font-size: 12px;
    font-weight: 600;
    text-align: start;
    cursor: pointer;

    &:hover {
      background-color: ${blueGreyMui._100};
    }

    &:focus-visible {
      outline: 2px solid ${greyMui.A700};
      outline-offset: -2px;
    }

    @media (prefers-reduced-motion: no-preference) {
      transition: background-color 0.2s ease;
    }
  }

  .station-timing__expander-caret {
    display: inline-block;
    color: ${blueGreyMui._600};
  }

  /* The caret glyph is not mirrored by the RTL plugin, so flip it with the layout */
  html[dir="rtl"] & .station-timing__expander-caret {
    transform: scaleX(-1);
  }

  /* Sits inside its own leg's cell, unlike the table-wide "legs without data" bar at the bottom, so it
     is quieter and carries no background of its own. */
  .station-timing__support {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 2px 10px;
    margin: 4px 0 0;
    color: ${blueGreyMui._700};
    font-size: 11px;
  }

  .station-timing__spread {
    display: inline-flex;
    align-items: baseline;
    gap: 4px;
  }

  .station-timing__day-toggle {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 44px;
    padding: 0;
    border: none;
    background-color: transparent;
    color: ${deepPurpleMui._700};
    font-size: 11px;
    font-weight: 600;
    text-align: start;
    text-decoration: underline;
    text-underline-offset: 3px;
    cursor: pointer;

    &:hover {
      color: ${deepPurpleMui._900};
    }

    &:focus-visible {
      outline: 2px solid ${greyMui.A700};
      outline-offset: 2px;
    }

    @media (prefers-reduced-motion: no-preference) {
      transition: color 0.2s ease;
    }
  }

  .station-timing__day-row {
    background-color: ${greyMui._50};
  }

  .station-timing__table th.station-timing__day {
    padding-inline-start: 30px;
    border-inline-start: 3px solid ${blueGreyMui._100};
    font-weight: 600;
    font-size: 11.5px;
    color: ${blueGreyMui._800};
  }

  .station-timing__day-name {
    white-space: nowrap;
  }

  /* Flex gaps rather than margins, so the Hebrew layout spaces the two figures the same way. */
  .station-timing__day-measure {
    display: inline-flex;
    align-items: baseline;
    gap: 6px;
  }

  .station-timing__day-median {
    font-weight: 700;
  }

  .station-timing__day-hint {
    display: inline-flex;
    align-items: baseline;
    gap: 4px;
    color: ${blueGreyMui._700};
    font-size: 11px;
  }

  .station-timing__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 7px;
  }

  /* The label is only a wrapper: the body next to the input is the chip the user sees, which is what
     lets a real radio stay in the markup and still drive both the selected look and the focus ring. */
  .station-timing__chip {
    position: relative;
    display: inline-flex;
    margin: 0;
    cursor: pointer;
  }

  .station-timing__chip input {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: 0;
    opacity: 0;
    pointer-events: none;
  }

  .station-timing__chip-body {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1px;
    min-width: 78px;
    min-height: 44px;
    padding: 4px 11px;
    border: 1px solid ${blueGreyMui._100};
    border-radius: 8px;
    background-color: ${rydeColors.white};

    @media (prefers-reduced-motion: no-preference) {
      transition:
        background-color 0.2s ease,
        border-color 0.2s ease;
    }
  }

  .station-timing__chip-value {
    color: ${blueGreyMui._900};
    font-size: 15px;
    font-weight: 700;
    line-height: 1.15;
  }

  .station-timing__chip-label {
    display: inline-flex;
    align-items: baseline;
    gap: 3px;
    color: ${blueGreyMui._700};
    font-size: 10px;
    line-height: 1.3;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  .station-timing__chip:hover .station-timing__chip-body {
    background-color: ${blueGreyMui._50};
  }

  .station-timing__chip input:checked + .station-timing__chip-body {
    border-color: ${deepPurpleMui._400};
    background-color: ${deepPurpleMui._50};
    box-shadow: inset 0 0 0 1px ${deepPurpleMui._400};
  }

  .station-timing__chip
    input:checked
    + .station-timing__chip-body
    .station-timing__chip-value,
  .station-timing__chip
    input:checked
    + .station-timing__chip-body
    .station-timing__chip-label {
    color: ${deepPurpleMui._700};
  }

  .station-timing__chip input:focus-visible + .station-timing__chip-body {
    outline: 2px solid ${greyMui.A700};
    outline-offset: 2px;
  }

  /* Backing a single leg out of the selection is an escape hatch, not a fourth measurement. */
  .station-timing__chip--planned .station-timing__chip-body {
    border-style: dashed;
    background-color: transparent;
  }

  .station-timing__chip--planned .station-timing__chip-value {
    color: ${blueGreyMui._700};
    font-weight: 600;
  }

  .station-timing__delta-cell {
    white-space: nowrap;
  }

  /* Keeps the pill and the share under it on the same edge in both directions. */
  .station-timing__delta-stack {
    display: inline-flex;
    flex-direction: column;
    align-items: flex-end;
  }

  .station-timing__delta {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 9px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 700;
  }

  .station-timing__delta-arrow {
    line-height: 1;
  }

  .station-timing__delta--slower {
    background-color: ${redMui._50};
    color: ${redMui._800};
  }

  .station-timing__delta--faster {
    background-color: ${blueMui._50};
    color: ${blueMui._800};
  }

  .station-timing__delta--on-plan {
    background-color: ${greenMui._50};
    color: ${greenMui._800};
  }

  .station-timing__delta--no-plan {
    background-color: ${blueGreyMui._50};
    color: ${blueGreyMui._800};
  }

  /* Day lines repeat their leg's comparison, so they carry it as plain coloured text. */
  .station-timing__delta--compact {
    padding: 0;
    background-color: transparent;
    font-size: 11px;
  }

  .station-timing__delta-sub {
    margin-block-start: 2px;
    color: ${blueGreyMui._700};
    font-size: 11px;
  }
`;
